/** @jsxImportSource @emotion/react */
import { ClassNames, useTheme } from "@emotion/react";
import {
  Checkbox as MuiCheckbox,
  FormControlLabel,
  FormHelperText,
} from "@material-ui/core";

import {
  ControlledFormFieldProps,
  StylableProps,
} from "@rewards-web/shared/types";

import { useFormControlContext } from "../form/form-control";
import { Tooltip } from "../tooltip";
import { Typography } from "../typography";

export interface CheckboxFieldProps
  extends ControlledFormFieldProps,
    StylableProps {
  label?: React.ReactNode;
  labelColor?: string;
  display?: "block" | "inline";
  disabled?: boolean;
  fontWeight?: number;
  disabledTooltipTitle?: string;
  // @default "center"
  checkboxAlignment?: "start" | "center";
  addGap?: boolean;
}

export function CheckboxField({
  display = "inline",
  label,
  labelColor,
  value,
  onChange,
  disabled = false,
  fontWeight = 800,
  disabledTooltipTitle,
  className,
  checkboxAlignment = "center",
  addGap = false,
  error,
}: CheckboxFieldProps) {
  const { submitting } = useFormControlContext();
  const theme = useTheme();

  return (
    <ClassNames>
      {({ css, cx }) => (
        <div>
          <Tooltip
            disabled={!disabled || !disabledTooltipTitle}
            title={disabledTooltipTitle ?? ""}
            placement="right"
          >
            <FormControlLabel
              classes={{
                label: css`
                  font-weight: ${fontWeight};
                `,
              }}
              className={cx(
                className,
                css`
                  display: ${display === "block" ? "flex" : "inline-flex"};
                  ${checkboxAlignment === "start"
                    ? "align-items: flex-start;"
                    : ""}
                `
              )}
              label={<Typography color={labelColor}>{label}</Typography>}
              control={
                <MuiCheckbox
                  disabled={disabled || submitting}
                  color={error ? "secondary" : "primary"}
                  classes={{
                    colorSecondary: css`
                      color: ${theme.palette.error.main};
                    `,
                  }}
                  checked={value !== undefined ? !!value : undefined}
                  onChange={
                    onChange ? (_, checked) => onChange(checked) : undefined
                  }
                  className={css`
                    padding: 6px;
                    ${addGap && `margin-right: ${theme.spacing(1)}`}
                  `}
                />
              }
            />
            {error && (
              <FormHelperText error={true} variant="filled">
                {error.message}
              </FormHelperText>
            )}
          </Tooltip>
        </div>
      )}
    </ClassNames>
  );
}
